/* ============================================================
   brellie "BUY MORE & SAVE MORE" buy-boxes — clean reimplementation
   of the original (Shrine) <quantity-breaks> web component.
   - tier radios set the add-to-cart quantity
   - per-unit <select>s resolve to real variants (greys out unavailable)
   - mixed-color multipacks add as SEPARATE line items via /cart/add.js
   - tier prices/compare recompute live when the base variant changes
   Zero Shrine dependency. Fails safe: never blocks native add-to-cart.
   ============================================================ */
(function () {
  function money(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var fmt = format || '${{amount}}';
    function withCommas(n) {
      return n.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }
    function fixed(value, precision) {
      var n = (value / 100).toFixed(precision);
      var parts = n.split('.');
      return withCommas(parts[0]) + (parts[1] ? '.' + parts[1] : '');
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case 'amount': return fixed(cents, 2);
        case 'amount_no_decimals': return fixed(cents, 0);
        case 'amount_with_comma_separator': return fixed(cents, 2).replace(/\./g, '#').replace(/,/g, '.').replace(/#/g, ',');
        case 'amount_no_decimals_with_comma_separator': return fixed(cents, 0).replace(/,/g, '.');
        case 'amount_with_space_separator': return fixed(cents, 2).replace(/,/g, ' ');
        case 'amount_no_decimals_with_space_separator': return fixed(cents, 0).replace(/,/g, ' ');
        default: return fixed(cents, 2);
      }
    });
  }

  function rootPath() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  class QuantityBreaks extends HTMLElement {
    connectedCallback() {
      try {
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.updatePrices = this.dataset.updatePrices === 'true';
        this.updatePdpPrices = this.dataset.updatePdpPrices === 'true';
        this.sectionId = this.dataset.section;

        var json = this.querySelector('script[type="application/json"]');
        this.variants = json ? JSON.parse(json.textContent) : [];

        this.form = document.getElementById('product-form-' + this.sectionId)
          || this.closest('.product__info-container')?.querySelector('form[action*="/cart/add"]')
          || document.querySelector('form[action*="/cart/add"]');

        this.radios = Array.from(this.querySelectorAll('input[type="radio"][name="quantity"]'));
        this.cards = Array.from(this.querySelectorAll('.quantity-break'));
        this.isAdding = false;

        this.initSelects();
        this.bindVariantWatch();
        this.bindPdpPriceWatch();
        this.bindAddToCart();
        this.refreshPrices(); // sync to current base on load
      } catch (e) {
        /* never let the buy-box break the page */
        console.warn('[quantity-breaks] init failed, falling back to native form', e);
      }
    }

    /* --- current base variant (from the product form's hidden id input) --- */
    currentVariant() {
      var idInput = this.form && this.form.querySelector('input[name="id"]');
      var id = idInput ? parseInt(idInput.value, 10) : null;
      var v = id && this.variants.find(function (x) { return x.id === id; });
      return v || this.variants.find(function (x) { return x.available; }) || this.variants[0];
    }

    /* --- recompute every tier's price/compare/savings.
       KindWood upgrade: tiers with per-unit selectors SUM each selected
       unit's own variant price (sizes cost different amounts), apply the
       tier's percentage (pay-2-of-3 = 33.33% off etc.), and display a
       blended "<each> / each" like the competitor reference. --- */
    refreshPrices() {
      if (!this.updatePrices && !this.updatePdpPrices) return;
      var base = this.currentVariant();
      if (!base) return;
      var self = this;
      this.cards.forEach(function (card) {
        var qty = parseInt(card.dataset.quantity, 10) || 1;
        var pl = parseFloat(card.dataset.percentageLeft);
        if (isNaN(pl)) pl = 1;
        var fixedOff = parseFloat(card.dataset.fixedDiscount) || 0;
        var compareMode = card.dataset.compareMode;

        function unitCompareOf(v) {
          if (compareMode === 'compare_price' && v.compare_at_price && v.compare_at_price > v.price) {
            return v.compare_at_price;
          }
          return v.price;
        }

        /* the units in this tier: resolve each per-unit selector to its
           variant; tiers without selectors are qty x the base variant */
        var units = [];
        var itemGroups = card.querySelectorAll('.quantity-break__selector-item');
        if (itemGroups.length) {
          itemGroups.forEach(function (item) {
            var values = Array.from(item.querySelectorAll('.quantity-break__variant-select')).map(function (s) { return s.value; });
            units.push(self.matchVariant(values) || base);
          });
          while (units.length < qty) units.push(base);
        } else {
          for (var i = 0; i < qty; i++) units.push(base);
        }

        var sum = 0, compareSum = 0;
        units.forEach(function (v) { sum += v.price; compareSum += unitCompareOf(v); });
        var price = Math.round(sum * pl - fixedOff);
        var compare = compareSum;
        var saved = compare - price;
        var each = Math.round(price / qty);
        var eachCompare = Math.round(compare / qty);
        var displayEach = qty > 1 || card.dataset.input === 'input_1';

        self.setText(card, '.quantity-break__price',
          displayEach ? money(each, self.moneyFormat) + ' / each' : money(price, self.moneyFormat));
        self.setText(card, '.quantity-break__compare-price',
          compare > price ? (displayEach ? money(eachCompare, self.moneyFormat) + ' / each' : money(compare, self.moneyFormat)) : '');

        var caption = card.querySelector('.quantity-break__caption');
        if (caption && caption.dataset.text != null) {
          var savedRounded = Math.round(saved / 100) * 100;
          caption.textContent = caption.dataset.text.replace(/\s*[—-]\s*you save\s*\[amount_saved\]/i, '')
            .replace(/\[total\]/g, money(price, self.moneyFormat))
            .replace(/\[amount_saved\]/g, money(saved, self.moneyFormat))
            .replace(/\[amount_saved_rounded\]/g, money(savedRounded, self.moneyFormat));
        }

        var savingsPill = card.querySelector('[data-savings-pill]');
        if (savingsPill) {
          savingsPill.textContent = saved > 0 ? 'Save ' + Math.round(saved * 100 / compare) + '%' : '';
          savingsPill.hidden = saved <= 0;
        }

        if (self.updatePdpPrices && card === self.selectedCard()) {
          self.updatePdpPrice(price, compare);
        }
      });
    }

    /* The headline price always represents the exact offer currently selected,
       including a blended/mixed-size bundle total. */
    updatePdpPrice(price, compare) {
      var priceRow = document.querySelector('[data-brellie-price]');
      if (!priceRow) return;
      var current = priceRow.querySelector('[data-brellie-price-current]');
      var compareEl = priceRow.querySelector('[data-brellie-price-compare]');
      var saveEl = priceRow.querySelector('[data-brellie-price-save]');
      if (current) current.textContent = money(price, this.moneyFormat);

      var hasSaving = compare > price;
      if (compareEl) {
        compareEl.textContent = hasSaving ? money(compare, this.moneyFormat) : '';
        compareEl.hidden = !hasSaving;
      }
      if (saveEl) {
        saveEl.textContent = hasSaving ? 'SAVE ' + Math.round((compare - price) * 100 / compare) + '%' : '';
        saveEl.hidden = !hasSaving;
      }
    }

    bindPdpPriceWatch() {
      if (!this.updatePdpPrices) return;
      var self = this;
      this.radios.forEach(function (radio) {
        radio.addEventListener('change', function () { self.refreshPrices(); });
      });
    }

    setText(card, sel, value) {
      var el = card.querySelector(sel);
      if (el) el.textContent = value;
    }

    /* --- when the main variant picker changes, recompute prices --- */
    bindVariantWatch() {
      if (!this.form) return;
      var self = this;
      this.form.addEventListener('change', function () {
        // let Dawn update the hidden id input first
        setTimeout(function () { self.refreshPrices(); self.refreshSelectAvailability(); }, 0);
      });
    }

    /* --- per-unit <select>s: bind change + initial availability --- */
    initSelects() {
      var self = this;
      this.querySelectorAll('.quantity-break__variant-select').forEach(function (sel) {
        sel.addEventListener('change', function () {
          self.cascadeBundleSelection(sel);
          self.refreshSelectAvailability();
          self.refreshPrices(); // sizes cost different amounts -> reprice the card live
        });
      });
      this.refreshSelectAvailability();
    }

    /* Buy 2 / Buy 3 are configured as a set. Choosing a size for one chew
       carries that choice forward through the remaining selectors, while any
       sizes above it are deliberately left alone for mixed bundles. */
    cascadeBundleSelection(select) {
      var card = select.closest('.quantity-break--bundle');
      var sourceItem = select.closest('.quantity-break__selector-item');
      if (!card || !sourceItem) return;

      var selectorItems = Array.from(card.querySelectorAll('.quantity-break__selector-item'));
      var sourceIndex = selectorItems.indexOf(sourceItem);
      var selectIndex = Array.from(sourceItem.querySelectorAll('.quantity-break__variant-select')).indexOf(select);
      if (sourceIndex < 0 || selectIndex < 0) return;

      selectorItems.slice(sourceIndex + 1).forEach(function (item) {
        var target = item.querySelectorAll('.quantity-break__variant-select')[selectIndex];
        if (!target) return;
        var hasValue = Array.from(target.options).some(function (option) { return option.value === select.value; });
        if (hasValue) target.value = select.value;
      });
    }

    /* grey out / disable option values that don't exist or are sold out,
       given the other selects within the same per-unit item */
    refreshSelectAvailability() {
      var self = this;
      this.querySelectorAll('.quantity-break__selector-item').forEach(function (item) {
        var selects = Array.from(item.querySelectorAll('.quantity-break__variant-select'));
        var chosen = selects.map(function (s) { return s.value; });
        selects.forEach(function (sel, idx) {
          Array.from(sel.options).forEach(function (opt) {
            var trial = chosen.slice();
            trial[idx] = opt.value;
            var v = self.matchVariant(trial);
            opt.classList.remove('non-existent', 'unavailable');
            if (!v) opt.classList.add('non-existent');
            else if (!v.available) opt.classList.add('unavailable');
          });
        });
      });
    }

    /* find a variant by an ordered list of option values */
    matchVariant(values) {
      return this.variants.find(function (v) {
        return values.every(function (val, i) {
          return val == null || v['option' + (i + 1)] === val;
        });
      });
    }

    selectedCard() {
      var checked = this.querySelector('input[type="radio"][name="quantity"]:checked');
      if (!checked) return null;
      return checked.nextElementSibling && checked.nextElementSibling.classList.contains('quantity-break')
        ? checked.nextElementSibling
        : this.querySelector('label[for="' + checked.id + '"]');
    }

    /* --- add-to-cart: intercept only when the selected tier has per-unit
       selectors (mixed-color multipack). Otherwise native qty-N submit. --- */
    bindAddToCart() {
      if (!this.form) return;
      var self = this;
      this.form.addEventListener('submit', function (e) {
        self.handleBundleAdd(e, e.submitter || self.form.querySelector('[type="submit"]'));
      }, true);

      /* Stop the button's native submit action before it reaches Dawn's
         product-form handler. The bundle has one authoritative /cart/add.js
         payload; it can never add the form's currently selected variant too. */
      var submitButton = this.form.querySelector('[type="submit"]');
      if (submitButton) {
        submitButton.addEventListener('click', function (e) {
          self.handleBundleAdd(e, submitButton);
        }, true);
      }
    }

    handleBundleAdd(e, button) {
      var card = this.selectedCard();
      if (!card) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      var items = this.buildItems(card);
      if (!items) {
        console.warn('[quantity-breaks] A selected bundle item could not be resolved.');
        return;
      }
      this.addItems(items, button);
    }

    /* build the multi-line item list for the selected tier, or null if this
       tier has no per-unit selectors (let the native form handle it) */
    buildItems(card) {
      var itemEls = Array.from(card.querySelectorAll('.quantity-break__selector-item'));
      if (!itemEls.length) return null;
      var self = this;
      var units = [];
      for (var i = 0; i < itemEls.length; i++) {
        var selects = Array.from(itemEls[i].querySelectorAll('.quantity-break__variant-select'));
        var values = selects.map(function (s) { return s.value; });
        var v = self.matchVariant(values);
        if (!v || !v.available) return null; // bail to native if anything unresolved
        units.push(v);
      }

      /* Shopify discounts only know about actual cart lines. Keep the paid
         and reward units separate so the automatic Buy X Get Y discount can
         zero the reward line and the drawer can show it as its own card. */
      var bundleConfig = {
        input_2: { paidUnits: 2, offer: 'Buy 2, Get 1 Free' },
        input_3: { paidUnits: 3, offer: 'Buy 3, Get 2 Free' }
      }[card.dataset.input];

      var groups = {};
      units.forEach(function (variant, index) {
        var role = bundleConfig && index >= bundleConfig.paidUnits ? 'free' : 'paid';
        var key = role + '-' + variant.id;
        if (!groups[key]) groups[key] = { id: variant.id, quantity: 0, role: role };
        groups[key].quantity += 1;
      });

      return Object.keys(groups).map(function (key) {
        var group = groups[key];
        var item = { id: parseInt(group.id, 10), quantity: group.quantity };
        if (bundleConfig) {
          item.properties = {
            _kw_bundle_offer: bundleConfig.offer,
            _kw_bundle_role: group.role
          };
        }
        return item;
      });
    }

    async addItems(items, button) {
      if (this.isAdding) return;
      this.isAdding = true;
      var root = rootPath();
      var drawer = document.querySelector('cart-drawer');
      if (button) button.setAttribute('aria-disabled', 'true'), button.classList.add('loading');
      try {
        var body = { items: items };
        if (drawer) {
          body.sections = ['cart-drawer', 'cart-icon-bubble'];
          body.sections_url = window.location.pathname;
        }
        var res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(body)
        });
        var data = await res.json();
        if (data.status) { // error (e.g. sold out)
          if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('loading'); }
          alert(data.description || data.message || 'Could not add to cart.');
          return;
        }
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          if (typeof drawer.renderContents === 'function') drawer.renderContents(data);
          if (typeof drawer.open === 'function') drawer.open();
        } else {
          window.location = root + 'cart';
        }
      } catch (err) {
        window.location = root + 'cart';
      } finally {
        this.isAdding = false;
        if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('loading'); }
      }
    }
  }

  if (!customElements.get('quantity-breaks')) {
    customElements.define('quantity-breaks', QuantityBreaks);
  }
})();
